/**
 * Theme-Hook — Light/Dark-Mode mit Persistence + System-Fallback.
 *
 * Quelle der Wahrheit ist `localStorage("strategify_theme")`. Solange dort
 * nichts steht, folgt die App `prefers-color-scheme` (System-Default) und
 * reagiert live auf Wechsel des OS-Themes. Sobald der User einmal aktiv
 * toggelt, wird die Wahl persistiert und überschreibt die System-Quelle.
 *
 * Der `<html>`-Class-Switch passiert hier; ein Inline-Script im `index.html`
 * setzt die Klasse schon vor React-Mount, damit kein FOUC entsteht — dieser
 * Hook hält die Klasse danach konsistent zum React-State.
 */
import { useCallback, useEffect, useState } from "react";

type Theme = "light" | "dark";
const STORAGE_KEY = "strategify_theme";

function getSystemTheme(): Theme {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function getInitialTheme(): { theme: Theme; explicit: boolean } {
  if (typeof window === "undefined") return { theme: "light", explicit: false };
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark") {
      return { theme: stored, explicit: true };
    }
  } catch {
    /* Privacy-Mode: localStorage blockiert — System-Default ist fine */
  }
  return { theme: getSystemTheme(), explicit: false };
}

function applyClass(theme: Theme): void {
  const root = document.documentElement;
  if (theme === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
}

export function useTheme(): {
  theme: Theme;
  isExplicit: boolean;
  toggle: () => void;
  reset: () => void;
} {
  const [state, setState] = useState(getInitialTheme);

  useEffect(() => {
    applyClass(state.theme);
  }, [state.theme]);

  // System-Theme live spiegeln, solange der User nichts explizit gesetzt hat.
  useEffect(() => {
    if (state.explicit) return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    function handler(event: MediaQueryListEvent): void {
      setState({ theme: event.matches ? "dark" : "light", explicit: false });
    }
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [state.explicit]);

  const toggle = useCallback(() => {
    setState((prev) => {
      const next: Theme = prev.theme === "dark" ? "light" : "dark";
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* siehe oben */
      }
      return { theme: next, explicit: true };
    });
  }, []);

  /** Zurück zu „folgt System" — entfernt die persistierte Wahl. */
  const reset = useCallback(() => {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* siehe oben */
    }
    setState({ theme: getSystemTheme(), explicit: false });
  }, []);

  return { theme: state.theme, isExplicit: state.explicit, toggle, reset };
}
